import React, { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'
import { debounce, cloneDeep } from 'lodash'
import shortid from 'shortid'

import config from 'environment'

import SaveService from 'services/save.service'
import ToastService from 'services/toast.service'
import PinService from 'services/pin.service'

import { CardType, CategoryType, defaultCard, isSecondaryCategory, OWNED, SaveStatus } from 'types/deck'
import { DOUBLE_FACED_LAYOUTS } from 'types/card'

import { setDeckStateAction } from 'app-redux/deck/actions'
import { useActionless, useActions, useAppSelector } from 'app-redux/hooks'
import { SET_ACTIVE_STATE } from 'app-redux/active/actions/types'
import { toggleModal as toggleModalAction } from 'app-redux/active/actions'
import { useCanEditDeck } from 'app-redux/deck/selectors'

import DeckCardContextMenu from 'components/card/deckCards/deckCardContextMenu'
import ReportModal from 'components/misc/ReportModal'

import Analytics from 'utils/googleAnalytics'
import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'
import { generateDeckUrl } from 'utils/deckSeo'

import styles from './deckCardWrapper.module.scss'

type CardActions = {
  multiSelect: () => void
  cardClick: (e: React.MouseEvent, skipOpenOnClick?: boolean) => void
  rightClick: (e: React.MouseEvent) => void
  openCardModal: (opensToEditions?: boolean) => void
  incrementCard: () => void
  decrementCard: () => void
  changeCategory: (zone: string) => void
  copyCardName: () => void
  setNormal: () => void
  setFoil: () => void
  setEtched: () => void
  removeCard: () => void
  setFlipped: (flipped: boolean) => void
  addAsNewCard: () => void
  setLocalQuantity: (newQuantity: number) => void
  onCardUpdate: (updatedCard: CardType | CardType[], removedId?: string) => void
  makeNewCategory: () => void
  addToArchidektPins: () => void
  reportCard: () => void
  onDragStart?: () => void
  onDragEnd?: () => void
  onViewRelatedCards?: () => void
}

export type LocalCardData = {
  saving: SaveStatus
  quantity: number
  flipped: boolean
  anyMultiSelected: boolean
  isMultiSelected: boolean
  hasFinishOptions: boolean
  isNormal: boolean
  isFoil: boolean
  isEtched: boolean
  hasFoilOption: boolean
  hasEtchedOption: boolean
  isMaybeboardPrimary: boolean
  isSideboardPrimary: boolean
  cardError: boolean
  inSecondaryCategory: boolean
  isFlipCard: boolean
  ownsDeck: boolean
  canBeCompanion: boolean
  categoryNames: string[]
  antiHighlight: boolean
}

type LocalDeckData = {
  categories: Record<string, CategoryType>
}

type LocalUserData = {
  id: string | number | null
}

export type OverriddenActions = {
  onCardClick?: (e: React.MouseEvent<Element, MouseEvent>, skipOpenOnClick?: boolean) => void
  openCardModal?: (openMenuCardToEditions: boolean) => void
  onDragStart?: () => void
  onDragEnd?: () => void
  onViewRelatedCards?: () => void
}

export type DeckCardWrapperChildProps = {
  card: CardType
  stackName?: string
  localCardData: LocalCardData
  deckData: LocalDeckData
  actions: CardActions
  extraData: Record<string, any>
  userData: LocalUserData
}

type Props = {
  cardId: string
  stackName?: string
  component: React.FC<DeckCardWrapperChildProps>
  extraData?: Record<string, any> // used to pass class names to wrapped component
  standaloneCard?: CardType
  onCardUpdated?: (updatedCard: CardType) => Promise<void>
  actionsOverrides?: OverriddenActions
}

const DeckCardWrapper = memo(
  ({ cardId, stackName, component, extraData = {}, standaloneCard, actionsOverrides, onCardUpdated }: Props) => {
    const card = standaloneCard || useAppSelector(state => state.deck.cardMap[cardId]) || defaultCard
    const cardMap = useAppSelector(state => state.deck.cardMap)
    const multiSelectedIds = useAppSelector(state => state.active.multiSelectedIds)
    const cardError = useAppSelector(state => state.deck.warningCards[card.name])
    const selectedStackSortingOption = useAppSelector(state => state.deck.stack)
    const categories = useAppSelector(state => state.deck.categories)
    const canEditDeck = useCanEditDeck()
    const highlightedCards = useAppSelector(state => state.active.highlightedCards)
    const deckId = useAppSelector(state => state.deck.id)
    const deckName = useAppSelector(state => state.deck.name)
    const stackOverlayIds = useAppSelector(state => state.active.stackOverlayIds)

    const touched = useRef<boolean>(false)
    const keylistener = useRef<(e: KeyboardEvent) => void>(() => null)
    const hovered = useRef<boolean>(false)

    const [setDeckState] = useActions(setDeckStateAction)
    const [setActiveState] = useActionless(SET_ACTIVE_STATE)
    const [toggleModal] = useActions(toggleModalAction)

    const [saving, setSaving] = useState<SaveStatus>('saved')
    const [flipped, setFlipped] = useState(card.flippedDefault)
    const [localQuantity, setLocalQuantity] = useState(card.qty) // used while debouncing fast quantity changes
    const [reportCardOpen, setReportCardOpen] = useState(false)

    const [{ tbId: userId }] = useCookies(['tbId'])

    const anyMultiSelected = !!Object.keys(multiSelectedIds).length
    const isMultiSelected = !!multiSelectedIds[card.id]

    const hasFinishOptions = card.options.length > 1
    const isNormal = card.modifier === 'Normal'
    const isFoil = card.modifier === 'Foil'
    const isEtched = card.modifier === 'Etched'
    const hasFoilOption = card.options.includes('Foil')
    const hasEtchedOption = card.options.includes('Etched')

    const isMaybeboardPrimary = card.categories[0] === 'Maybeboard'
    const isSideboardPrimary = card.categories[0] === 'Sideboard'

    const inSecondaryCategory = isSecondaryCategory(selectedStackSortingOption, stackName, card)
    const isFlipCard = DOUBLE_FACED_LAYOUTS.includes(card.layout)

    const handleOpenCardDetails = (openMenuCardToEditions = false) => {
      if (actionsOverrides?.openCardModal) return actionsOverrides?.openCardModal(openMenuCardToEditions)

      Analytics.signalEvent(`Deck card wrapper - Opened card details from menu`)

      setDeckState({ cardMenuOpen: true, menuCardId: card.id, openMenuCardToEditions })
    }

    const handleMultiSelect = () => {
      if (extraData.noMultiSelect) return

      const updatedMultiSelectedIds = { ...multiSelectedIds }

      if (isMultiSelected) delete updatedMultiSelectedIds[card.id]
      else updatedMultiSelectedIds[card.id] = true

      setActiveState({ multiSelectedIds: updatedMultiSelectedIds })
    }

    const handleCardClick = (e: React.MouseEvent, skipOpenOnClick?: boolean) => {
      const modKeyHeld = config.getOnMac() ? e.metaKey : e.ctrlKey

      if (!modKeyHeld && !anyMultiSelected && skipOpenOnClick) return
      if (modKeyHeld || anyMultiSelected) return handleMultiSelect()

      Analytics.signalEvent(`Deck card wrapper - Opened card from clicking card`)

      setDeckState({ cardMenuOpen: true, menuCardId: card.id })
    }

    const handleRightClick = (e: React.MouseEvent) => {
      // Only supported on mobile
      if (!touched.current) return

      e.preventDefault()
      e.stopPropagation()

      handleMultiSelect()
    }

    const handleSetDebouncedQty = (qty: number) => {
      const removeId = qty < 1 ? card.id : undefined
      const updatedCard = qty < 1 ? [] : { ...card, qty }

      handleUpdateCard(updatedCard, removeId)

      if (qty < 1 && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }
    }

    // Reset local state if the cards quantity changes someway that isn't here
    // Reset the debounce method to make sure it has correct card info (since it's only updated when lodash.debounce is called)
    const debouncedQtyChange = useMemo(() => {
      if (card.qty !== localQuantity) setLocalQuantity(card.qty)

      return debounce(handleSetDebouncedQty, 660)
    }, [card])

    const handleIncrementCard = () => {
      setLocalQuantity(localQuantity + 1)
      debouncedQtyChange(localQuantity + 1)
    }

    const handleDecrementCard = () => {
      setLocalQuantity(localQuantity - 1 > 0 ? localQuantity - 1 : 0)
      debouncedQtyChange(localQuantity - 1)
    }

    const handleMoveToZone = (zone: string) => {
      if (card.categories[0] === zone && card.qty > 0) return

      const primaryCategoryName = card.categories[0]
      const primaryIncludedInDeck = categories[primaryCategoryName]?.includedInDeck

      const updatedCardCategories = [
        zone,
        ...card.categories.filter(existingCategoryName => {
          if (existingCategoryName === zone) return false

          // Don't remove a cards existing categories when it moves from that category _unless_ it's moving from side/maybeboard
          if (!primaryIncludedInDeck && existingCategoryName === primaryCategoryName) return false
          if (primaryCategoryName === 'Sideboard' && existingCategoryName === 'Sideboard') return false

          return true
        }),
      ]

      handleUpdateCard({ ...card, qty: card.qty || 1, categories: updatedCardCategories })
    }

    const handleMakeNewCategory = () => toggleModal('newCategoryDrop', { card, sourceStack: stackName })

    const handleCopyCardName = () => {
      navigator.clipboard.writeText(card.name)
      ToastService.create(`Copied ${card.name} to clipboard`, 'Deck Page', 'success')
    }

    const handleAddAsNewCard = () => handleUpdateCard({ ...card, deckRelationId: '', id: shortid.generate(), qty: 1 })
    const handleSetNormal = () => handleUpdateCard({ ...card, modifier: 'Normal' })
    const handleSetFoil = () => handleUpdateCard({ ...card, modifier: 'Foil' })
    const handleSetEtched = () => handleUpdateCard({ ...card, modifier: 'Etched' })
    const handleRemoveCard = () => handleUpdateCard([], card.id)

    const handlePinCard = () => {
      if (card.pinnedStatus !== 0)
        return setActiveState({ pinnedCardsOverlay: true, pinnedCardsFilteredCards: [card.name] })

      PinService.add(card)
        .then(() => {
          ToastService.create(`Pinned ${card.name}`, 'Pinned Cards', 'success')

          // If we're in passthrough mode, just send the update up the chain
          if (standaloneCard && onCardUpdated) return onCardUpdated({ ...card, pinnedStatus: OWNED.YES })

          const updatedCardMap = cloneDeep(cardMap)
          updatedCardMap[cardId].pinnedStatus = OWNED.YES
          setDeckState({ cardMap: updatedCardMap })
        })
        .catch(err => ToastService.create(err.error, 'Pinned Cards', 'error'))
    }

    useEffect(() => {
      // If the stack overlay ever opens/ closes/ does anything, just unset the hovererd state. Opening it under certain circumstances can result in the hovered state not unsetting correctly
      hovered.current = false
    }, [stackOverlayIds])

    useEffect(() => {
      // Cleanup old listeners (we need to remove the old ones and create new ones when the state changes)
      window.removeEventListener('keydown', keylistener.current)

      // Update current listener (this way the listener has the updated redux/ local state)
      keylistener.current = (e: KeyboardEvent) => {
        if (!hovered.current) return

        const key = e.key.toLocaleUpperCase()

        // prettier-ignore
        const listenedForKeys = ['-', '_', '=', '+', 'R', 'M', 'S', 'F', 'N', 'E', 'L', 'A', 'O', 'P', 'C', 'W']

        if (e.ctrlKey || e.metaKey) return
        if (basicKeybindSkips(e)) return
        if (!listenedForKeys.includes(key)) return
        if (anyMultiSelected)
          return ToastService.create('Keybinds are disabled when multiselecting', 'Deck Page', 'warning')

        if (isFlipCard && key === 'L') setFlipped(!flipped)
        if (key === 'O') handleOpenCardDetails(false)
        if (key === 'P') handleOpenCardDetails(true)

        if (key === 'C') handleCopyCardName()

        if (!canEditDeck) return // the rest of the actions deal with editing the deck, if they don't have write access, we can ignore the rest

        if (key === '-' || key === '_') handleDecrementCard()
        if (key === '=' || key === '+') handleIncrementCard()
        if (key === 'R') handleUpdateCard([], card.id)

        if (key === 'W' && !card.customCard) handlePinCard()

        if (key === 'A') {
          // Card isn't in the deck yet, add it to the default category
          if (card.qty === 0)
            handleMoveToZone(card.globalCategories[0] || card.defaultCategory || card.typeCategory || '😎')

          // Card is in the deck, move it back to whatever category it was in before (or the default)
          if (isMaybeboardPrimary || isSideboardPrimary)
            handleMoveToZone(
              card.categories[1] || card.globalCategories[0] || card.defaultCategory || card.typeCategory || '😎',
            )
        }
        if (!isMaybeboardPrimary && key === 'M') handleMoveToZone('Maybeboard')
        if (!isSideboardPrimary && key === 'S') handleMoveToZone('Sideboard')

        if (!isFoil && hasFoilOption && key === 'F') handleSetFoil()
        if (!isNormal && hasFinishOptions && key === 'N') handleSetNormal()
        if (!isEtched && hasEtchedOption && key === 'E') handleSetEtched()
      }

      window.addEventListener('keydown', keylistener.current)

      return () => window.removeEventListener('keydown', keylistener.current)
    }, [localQuantity, card, flipped, canEditDeck, anyMultiSelected])

    useEffect(() => {
      setFlipped(card.flippedDefault)
    }, [card.flippedDefault])

    const handleUpdateCard = (updatedCard: CardType | CardType[], removedId?: string) => {
      setSaving('saving')

      const request =
        onCardUpdated && standaloneCard && !Array.isArray(updatedCard)
          ? onCardUpdated(updatedCard)
          : SaveService.save(updatedCard, removedId)

      request.then(() => setSaving('saved')).catch(() => setSaving('error'))
    }

    const actions: CardActions = {
      multiSelect: handleMultiSelect,
      cardClick: actionsOverrides?.onCardClick || handleCardClick,
      rightClick: handleRightClick,
      incrementCard: handleIncrementCard,
      decrementCard: handleDecrementCard,
      changeCategory: handleMoveToZone,
      copyCardName: handleCopyCardName,
      openCardModal: handleOpenCardDetails,
      setNormal: handleSetNormal,
      setFoil: handleSetFoil,
      setEtched: handleSetEtched,
      removeCard: handleRemoveCard,
      addAsNewCard: handleAddAsNewCard,
      setFlipped,
      setLocalQuantity,
      onCardUpdate: handleUpdateCard,
      makeNewCategory: handleMakeNewCategory,
      addToArchidektPins: handlePinCard,
      reportCard: () => setReportCardOpen(true),
      onDragStart: actionsOverrides?.onDragStart,
      onDragEnd: actionsOverrides?.onDragEnd,
      onViewRelatedCards: actionsOverrides?.onViewRelatedCards,
    }

    const cardData: LocalCardData = {
      quantity: localQuantity,
      flipped,
      anyMultiSelected,
      isMultiSelected,
      hasFinishOptions,
      isNormal,
      isFoil,
      isEtched,
      hasFoilOption,
      hasEtchedOption,
      isMaybeboardPrimary,
      isSideboardPrimary,
      cardError,
      inSecondaryCategory,
      isFlipCard,
      ownsDeck: canEditDeck,
      canBeCompanion: card.text.includes('Companion —'),
      categoryNames: Object.keys(categories).sort(),
      saving,
      antiHighlight: !!Object.keys(highlightedCards).length && !highlightedCards[card.id],
    }

    const deckData = { categories }
    const userData = { id: userId }

    const Component = component

    return (
      <div
        className={`${styles.container} ${extraData.wrapperClassName}`}
        onMouseEnter={() => {
          hovered.current = true

          // Don't do this unless the card is in the deck (AKA not a card in search)
          if (card.deckRelationId && card.qty)
            setTimeout(() => {
              if (!hovered.current) return

              setActiveState({ hoveredCardId: card.id })
            }, 250)
        }}
        onMouseLeave={() => (hovered.current = false)}
        onTouchStart={() => {
          touched.current = true

          if (card.deckRelationId && card.qty) setActiveState({ hoveredCardId: card.id })
        }}
        onTouchEnd={() => (touched.current = false)}>
        <DeckCardContextMenu
          checkSkipRightClick={() => !touched.current && !anyMultiSelected}
          wrapperData={{ card, localCardData: cardData, deckData, stackName, actions, extraData, userData }}>
          <Component
            card={card}
            localCardData={cardData}
            stackName={stackName}
            actions={actions}
            extraData={extraData}
            deckData={deckData}
            userData={userData}
          />
        </DeckCardContextMenu>

        <ReportModal
          open={reportCardOpen}
          onClose={() => setReportCardOpen(false)}
          header={`Report ${card.name}`}
          url={generateDeckUrl(deckId, deckName)}
          type="Card"
        />
      </div>
    )
  },
)

export default DeckCardWrapper
